const SAFE_COLOR_RE = /^(#[0-9a-fA-F]{3,8}|rgb\(([^)]+)\)|rgba\(([^)]+)\)|hsl\(([^)]+)\)|hsla\(([^)]+)\)|[a-zA-Z]+)$/;
const SAFE_FONT_SIZE_RE = /^(?:[8-9]|[1-5][0-9]|6[0-4])px$/;
const SAFE_FONT_WEIGHT_RE = /^(normal|bold|[1-9]00)$/;

export function escapeHtml(text: string): string {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function plainTextToNoteHtml(text: string): string {
  return escapeHtml(String(text || '')).replace(/\r?\n/g, '<br>');
}

function sanitizeStyle(styleRaw: string): string {
  const out: string[] = [];
  for (const chunk of String(styleRaw || '').split(';')) {
    const idx = chunk.indexOf(':');
    if (idx <= 0) continue;
    const key = chunk.slice(0, idx).trim().toLowerCase();
    const value = chunk.slice(idx + 1).trim();
    if (!value) continue;

    if (key === 'color' && SAFE_COLOR_RE.test(value)) {
      out.push(`color:${value}`);
      continue;
    }
    if (key === 'font-size' && SAFE_FONT_SIZE_RE.test(value)) {
      out.push(`font-size:${value}`);
      continue;
    }
    if (key === 'font-weight' && SAFE_FONT_WEIGHT_RE.test(value)) {
      out.push(`font-weight:${value}`);
      continue;
    }
  }
  return out.join(';');
}

function sanitizeNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return escapeHtml(node.textContent || '');
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();

  if (tag === 'br') return '<br>';

  const inner = Array.from(el.childNodes).map(sanitizeNode).join('');
  if (tag !== 'span') return inner;

  const safeStyle = sanitizeStyle(el.getAttribute('style') || '');
  if (!safeStyle) return inner;
  return `<span style="${escapeHtml(safeStyle)}">${inner}</span>`;
}

function sanitizeChildren(nodes: NodeListOf<ChildNode> | ChildNode[]): string {
  let out = '';

  for (const node of Array.from(nodes)) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      const tag = element.tagName.toLowerCase();
      if (tag === 'div' || tag === 'p') {
        const inner = sanitizeChildren(Array.from(element.childNodes));
        if (out && !out.endsWith('<br>')) out += '<br>';
        out += inner;
        if (!out.endsWith('<br>')) out += '<br>';
        continue;
      }
    }

    out += sanitizeNode(node);
  }

  return out;
}

export function sanitizeNoteHtml(raw: string): string {
  const input = String(raw || '');
  if (!input) return '';

  if (typeof DOMParser === 'undefined') {
    return plainTextToNoteHtml(input);
  }

  try {
    const doc = new DOMParser().parseFromString(`<div>${input}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!root) return '';
    const sanitized = sanitizeChildren(Array.from(root.childNodes))
      .replace(/(?:<br>){3,}/g, '<br><br>')
      .replace(/^(?:<br>)+/, '')
      .replace(/(?:<br>)+$/, '');
    return sanitized;
  } catch {
    return plainTextToNoteHtml(input);
  }
}

export function noteHtmlToPlainText(html: string): string {
  const safe = sanitizeNoteHtml(html);
  if (!safe) return '';
  if (typeof DOMParser === 'undefined') {
    return safe.replace(/<br\s*\/?\s*>/gi, '\n').replace(/<[^>]+>/g, '');
  }
  try {
    const doc = new DOMParser().parseFromString(`<div>${safe}</div>`, 'text/html');
    return (doc.body.textContent || '').replace(/\u00A0/g, ' ');
  } catch {
    return safe.replace(/<br\s*\/?\s*>/gi, '\n').replace(/<[^>]+>/g, '');
  }
}

export function getDrawingNoteHtml(drawing: { text?: string; textHtml?: string }): string {
  if (typeof drawing.textHtml === 'string' && drawing.textHtml.trim()) {
    return sanitizeNoteHtml(drawing.textHtml);
  }
  if (typeof drawing.text === 'string' && drawing.text.trim()) {
    if (/[<>]/.test(drawing.text)) return sanitizeNoteHtml(drawing.text);
    return plainTextToNoteHtml(drawing.text);
  }
  return '';
}
